import type { Params } from "react-router";

/**
 * Pages reached as a detour from the work (F87): the profile, administration, connecting a
 * PC, a review, an item, a release. Their route says so in its `handle`; a layout route that
 * says so covers every page under it. The shell then shows one back control above the page,
 * which returns to the place the detour started from.
 */
export interface DetourHandle {
  readonly detour: true;
  /**
   * Where Back goes when no work place is known, as on a page another site sent the browser
   * to; the start page when unset. From the route's own params where the place depends on
   * them, as a batch's vita returns to its batch.
   */
  readonly returnTo?: string | ((params: Readonly<Params>) => string);
}

/** A route `handle` that also marks its pages as a detour. */
export function asDetour<THandle extends object>(handle: THandle, returnTo?: DetourHandle["returnTo"]): THandle & DetourHandle {
  return returnTo === undefined ? { ...handle, detour: true } : { ...handle, detour: true, returnTo };
}

/** Where Back goes on the matched detour when no work place is known, with the deepest route's params; `null` when it names none. */
export function detourReturnOfMatches(matches: readonly { readonly handle: unknown; readonly params?: Readonly<Params> }[]): string | null {
  const returnTo = detourOfMatches(matches)?.returnTo;
  if (returnTo === undefined) return null;
  return typeof returnTo === "string" ? returnTo : returnTo(matches.at(-1)?.params ?? {});
}

/** The deepest matched route's detour, or `null` on a page of the work. */
export function detourOfMatches(matches: readonly { readonly handle: unknown }[]): DetourHandle | null {
  for (const { handle } of [...matches].reverse()) {
    if (typeof handle === "object" && handle !== null && "detour" in handle && handle.detour === true) return handle as DetourHandle;
  }
  return null;
}
